import COLORS from 'constants/colors';
import { MONTHS } from 'constants/constants';

// Website Metrics dashboard — shared constants.
// Funnel: QL (qualified valid website-organic signups) -> Trial Scheduled -> Trial Done -> Enrolled.

export const BASE_PATH = '/website-metrics';

// Source categories == mbt_leads.channel values (all organic, website signups).
export const CHANNELS = {
  BRAND: 'Organic Brand',
  INTENT: 'Organic Intent',
  NON_CONTENT: 'Organic Non-Content',
  CONTENT: 'Organic Content',
  BLOG: 'Organic Blog',
};

// Short display label per channel.
export const CHANNEL_LABELS = {
  [CHANNELS.BRAND]: 'Brand',
  [CHANNELS.INTENT]: 'Intent',
  [CHANNELS.NON_CONTENT]: 'Non-Content',
  [CHANNELS.CONTENT]: 'Content',
  [CHANNELS.BLOG]: 'Blog',
};

// Colours mirror the website-organic-performance.html reference. Any channel
// not listed here falls back to DEFAULT_SOURCE_COLOR.
export const SOURCE_COLORS = {
  [CHANNELS.BRAND]: '#3b82f6',
  [CHANNELS.INTENT]: '#10b981',
  [CHANNELS.NON_CONTENT]: '#f59e0b',
  [CHANNELS.CONTENT]: '#8b5cf6',
  [CHANNELS.BLOG]: '#ec4899',
};

export const DEFAULT_SOURCE_COLOR = '#64748b';

export const ALL_CHANNELS = [
  CHANNELS.BRAND,
  CHANNELS.INTENT,
  CHANNELS.NON_CONTENT,
  CHANNELS.CONTENT,
];

// In-page tabs. `channel: null` => Summary (all sources).
export const TABS = [
  { key: 'summary', label: 'Summary', channel: null },
  { key: 'brand', label: 'Brand', channel: CHANNELS.BRAND },
  { key: 'intent', label: 'Intent', channel: CHANNELS.INTENT },
  { key: 'non-content', label: 'Non-Content', channel: CHANNELS.NON_CONTENT },
  { key: 'content', label: 'Content', channel: CHANNELS.CONTENT },
  { key: 'blogs', label: 'Blogs', channel: CHANNELS.BLOG },
  { key: 'page-analysis', label: 'Page Analysis', channel: null },
  { key: 'experiment-analysis', label: 'Experiment Analysis', channel: null },
];

// Page Analysis: mbt history is reliable from April 2025 — the range picker
// does not allow earlier dates.
export const ANALYSIS_MIN_DATE = new Date(2025, 3, 1);

export const DEFAULT_TAB = 'summary';

// Funnel metric field keys (match the insightsreader response) + display labels.
export const METRICS = {
  QL: 'ql',
  TRIAL_SCHEDULED: 'trial_scheduled',
  TRIAL_DONE: 'trial_done',
  ENROLLED: 'enrolled',
};

// The four funnel metric keys in funnel order — single source of truth for
// every place that iterates the metrics (metricFields, sumRows, pivots, graphs).
export const METRIC_KEYS = [
  METRICS.QL,
  METRICS.TRIAL_SCHEDULED,
  METRICS.TRIAL_DONE,
  METRICS.ENROLLED,
];

export const METRIC_LABELS = {
  [METRICS.QL]: 'QL',
  [METRICS.TRIAL_SCHEDULED]: 'Trials Scheduled',
  [METRICS.TRIAL_DONE]: 'Trials Done',
  [METRICS.ENROLLED]: 'Enrolled',
};

export const METRIC_COLORS = {
  [METRICS.QL]: '#3b82f6',
  [METRICS.TRIAL_SCHEDULED]: '#06b6d4',
  [METRICS.TRIAL_DONE]: '#10b981',
  [METRICS.ENROLLED]: '#8b5cf6',
};

// Conversion ratios shown as KPIs / line chart.
export const CONVERSIONS = [
  {
    key: 'ql_td',
    label: 'QL → Trial Done',
    numerator: METRICS.TRIAL_DONE,
    denominator: METRICS.QL,
    color: '#3b82f6',
  },
  {
    key: 'td_enr',
    label: 'Trial Done → Enrolled',
    numerator: METRICS.ENROLLED,
    denominator: METRICS.TRIAL_DONE,
    color: '#10b981',
  },
  {
    key: 'ql_enr',
    label: 'QL → Enrolled',
    numerator: METRICS.ENROLLED,
    denominator: METRICS.QL,
    color: '#f59e0b',
  },
];

// Region bucket codes (match insightsreader region_bucket derivation). Single
// source of truth: REGION_OPTIONS values AND COUNTRY_OPTIONS_BY_REGION keys are
// both built from these, so a code can never drift between the two.
export const REGIONS = {
  US: 'US',
  APAC: 'APAC',
  ME: 'ME',
  UK: 'UK',
  OTHERS: 'Others',
};

// Region buckets (match insightsreader region_bucket derivation).
export const REGION_OPTIONS = [
  { label: 'US', value: REGIONS.US },
  { label: 'APAC', value: REGIONS.APAC },
  { label: 'Middle East', value: REGIONS.ME },
  { label: 'UK / Europe', value: REGIONS.UK },
  { label: 'Others', value: REGIONS.OTHERS },
];

// is_old_user multi-select. Values are normalized server-side.
export const USER_TYPE_OPTIONS = [
  { label: 'New users', value: 'new' },
  { label: 'Returning users', value: 'returning' },
];

// Countries offered per region (ISO-2 codes; the backend filters on
// user_source_log.meta.opportunity_region_data.country). The grouping is
// GEOGRAPHIC — the dashboard's region_bucket comes from a different lineage
// (mbt_leads.region/country_bucket), so a country filter is applied INSTEAD of
// the region filter (see buildRangeParams/buildFunnelQuery), never ANDed with
// it. The lists cover every country seen in the organic cohort (12-month scan)
// plus common neighbours. Used only to populate the per-region country picker.
export const COUNTRY_OPTIONS_BY_REGION = {
  [REGIONS.US]: [
    { label: 'Argentina', value: 'AR' },
    { label: 'Bahamas', value: 'BS' },
    { label: 'Barbados', value: 'BB' },
    { label: 'Bermuda', value: 'BM' },
    { label: 'Brazil', value: 'BR' },
    { label: 'Canada', value: 'CA' },
    { label: 'Cayman Islands', value: 'KY' },
    { label: 'Chile', value: 'CL' },
    { label: 'Colombia', value: 'CO' },
    { label: 'Dominica', value: 'DM' },
    { label: 'Dominican Republic', value: 'DO' },
    { label: 'Ecuador', value: 'EC' },
    { label: 'Grenada', value: 'GD' },
    { label: 'Guyana', value: 'GY' },
    { label: 'Haiti', value: 'HT' },
    { label: 'Honduras', value: 'HN' },
    { label: 'Jamaica', value: 'JM' },
    { label: 'Mexico', value: 'MX' },
    { label: 'Peru', value: 'PE' },
    { label: 'Puerto Rico', value: 'PR' },
    { label: 'Sint Maarten', value: 'SX' },
    { label: 'St Kitts & Nevis', value: 'KN' },
    { label: 'St Vincent', value: 'VC' },
    { label: 'Suriname', value: 'SR' },
    { label: 'Trinidad & Tobago', value: 'TT' },
    { label: 'United States', value: 'US' },
    { label: 'Venezuela', value: 'VE' },
  ],
  [REGIONS.APAC]: [
    { label: 'American Samoa', value: 'AS' },
    { label: 'Australia', value: 'AU' },
    { label: 'Bangladesh', value: 'BD' },
    { label: 'Brunei', value: 'BN' },
    { label: 'Cambodia', value: 'KH' },
    { label: 'China', value: 'CN' },
    { label: 'Fiji', value: 'FJ' },
    { label: 'Hong Kong', value: 'HK' },
    { label: 'India', value: 'IN' },
    { label: 'Indonesia', value: 'ID' },
    { label: 'Japan', value: 'JP' },
    { label: 'Kazakhstan', value: 'KZ' },
    { label: 'Laos', value: 'LA' },
    { label: 'Malaysia', value: 'MY' },
    { label: 'Myanmar', value: 'MM' },
    { label: 'Nepal', value: 'NP' },
    { label: 'New Zealand', value: 'NZ' },
    { label: 'Pakistan', value: 'PK' },
    { label: 'Philippines', value: 'PH' },
    { label: 'Singapore', value: 'SG' },
    { label: 'South Korea', value: 'KR' },
    { label: 'Sri Lanka', value: 'LK' },
    { label: 'Taiwan', value: 'TW' },
    { label: 'Thailand', value: 'TH' },
    { label: 'Tonga', value: 'TO' },
    { label: 'Uzbekistan', value: 'UZ' },
    { label: 'Vietnam', value: 'VN' },
  ],
  [REGIONS.ME]: [
    { label: 'Azerbaijan', value: 'AZ' },
    { label: 'Bahrain', value: 'BH' },
    { label: 'Egypt', value: 'EG' },
    { label: 'Iraq', value: 'IQ' },
    { label: 'Israel', value: 'IL' },
    { label: 'Jordan', value: 'JO' },
    { label: 'Kuwait', value: 'KW' },
    { label: 'Lebanon', value: 'LB' },
    { label: 'Oman', value: 'OM' },
    { label: 'Palestine', value: 'PS' },
    { label: 'Qatar', value: 'QA' },
    { label: 'Saudi Arabia', value: 'SA' },
    { label: 'United Arab Emirates', value: 'AE' },
    { label: 'Yemen', value: 'YE' },
  ],
  [REGIONS.UK]: [
    { label: 'Albania', value: 'AL' },
    { label: 'Austria', value: 'AT' },
    { label: 'Belgium', value: 'BE' },
    { label: 'Cyprus', value: 'CY' },
    { label: 'Czechia', value: 'CZ' },
    { label: 'Denmark', value: 'DK' },
    { label: 'Finland', value: 'FI' },
    { label: 'France', value: 'FR' },
    { label: 'Georgia', value: 'GE' },
    { label: 'Germany', value: 'DE' },
    { label: 'Gibraltar', value: 'GI' },
    { label: 'Greece', value: 'GR' },
    { label: 'Hungary', value: 'HU' },
    { label: 'Ireland', value: 'IE' },
    { label: 'Italy', value: 'IT' },
    { label: 'Latvia', value: 'LV' },
    { label: 'Liechtenstein', value: 'LI' },
    { label: 'Luxembourg', value: 'LU' },
    { label: 'Moldova', value: 'MD' },
    { label: 'Netherlands', value: 'NL' },
    { label: 'Norway', value: 'NO' },
    { label: 'Poland', value: 'PL' },
    { label: 'Portugal', value: 'PT' },
    { label: 'Romania', value: 'RO' },
    { label: 'Russia', value: 'RU' },
    { label: 'Slovakia', value: 'SK' },
    { label: 'Spain', value: 'ES' },
    { label: 'Sweden', value: 'SE' },
    { label: 'Switzerland', value: 'CH' },
    { label: 'Turkey', value: 'TR' },
    { label: 'Ukraine', value: 'UA' },
    { label: 'United Kingdom', value: 'GB' },
  ],
  [REGIONS.OTHERS]: [
    { label: 'Angola', value: 'AO' },
    { label: "Côte d'Ivoire", value: 'CI' },
    { label: 'Ethiopia', value: 'ET' },
    { label: 'Ghana', value: 'GH' },
    { label: 'Guinea', value: 'GN' },
    { label: 'Kenya', value: 'KE' },
    { label: 'Mauritius', value: 'MU' },
    { label: 'Mozambique', value: 'MZ' },
    { label: 'Nigeria', value: 'NG' },
    { label: 'Senegal', value: 'SN' },
    { label: 'Somalia', value: 'SO' },
    { label: 'South Africa', value: 'ZA' },
    { label: 'Sudan', value: 'SD' },
    { label: 'Tanzania', value: 'TZ' },
    { label: 'Uganda', value: 'UG' },
    { label: 'Zimbabwe', value: 'ZW' },
  ],
};

// De-duplicated country options for the union of the selected regions (used to
// populate the per-region country multiselect). Empty when no region is chosen.
export const countriesForRegions = (regions = []) => {
  const seen = {};
  const out = [];
  regions.forEach(region => {
    (COUNTRY_OPTIONS_BY_REGION[region] || []).forEach(option => {
      if (!seen[option.value]) {
        seen[option.value] = true;
        out.push(option);
      }
    });
  });
  return out;
};

// Keep only the country codes that still belong to the selected regions — so a
// stale selection is dropped once its region is de-selected. The single source
// of truth for turning (regions, countries) into the codes actually sent.
export const scopedCountryCodes = (regions = [], countries = []) => {
  if (!countries.length || !regions.length) return [];
  const allowed = {};
  countriesForRegions(regions).forEach(option => {
    allowed[option.value] = true;
  });
  return countries.filter(code => allowed[code]);
};

export const CHANNEL_OPTIONS = [...ALL_CHANNELS, CHANNELS.BLOG].map(channel => ({
  label: CHANNEL_LABELS[channel],
  value: channel,
}));

// Static organic sub-source options — DB-verified (12-month cross-tab) as the
// user_source_log.source_channel values that actually occur under Organic
// Non-Content, ordered by frequency. '(unknown)' = lead with no matching USL row.
export const SUB_SOURCE_OPTIONS = [
  { label: 'ORGANIC', value: 'ORGANIC' },
  { label: 'ORGANIC_WHATSAPP', value: 'ORGANIC_WHATSAPP' },
  { label: 'ORGANIC_SOCIAL', value: 'ORGANIC_SOCIAL' },
  { label: 'ORGANIC_INBOUND', value: 'ORGANIC_INBOUND' },
  { label: 'ORGANIC_TEACHER', value: 'ORGANIC_TEACHER' },
  { label: 'ORGANIC_TICKET', value: 'ORGANIC_TICKET' },
  { label: '(unknown)', value: '(unknown)' },
];

// '(unknown)' bucket — hover reasons per context. Sub-source: the lead's
// parent has NO user_source_log row at all (mostly CRM-era / agent-created
// leads). Pages: the USL row exists but the page was never captured client-side.
export const UNKNOWN_LABEL = '(unknown)';
export const UNKNOWN_REASONS = {
  SUB_SOURCE:
    'No tracking record (user_source_log) exists for this lead at all - mostly older CRM-era or agent-created leads - so the source cannot be attributed.',
  LANDING_PAGE:
    'No landing page was captured on this lead\u2019s tracking record - web tracking never initialised (blocked cookies, or non-web flows like the WhatsApp bot).',
  SIGNUP_PAGE:
    'No signup page was captured on this lead\u2019s tracking record, so the page it signed up on is unknown.',
  BLOG:
    'This lead is in the Blog channel but its tracking record carries no blog-referral tag (utm_source=blog-lead), so the specific blog cannot be identified - mostly agent-created leads or traffic misclassified into this channel.',
};

// Comparison period presets.
export const COMPARE_MODES = {
  NONE: 'none',
  PREV_PERIOD: 'prev_period',
  PREV_MONTH: 'prev_month',
  PREV_YEAR: 'prev_year',
  CUSTOM: 'custom',
};

export const COMPARE_OPTIONS = [
  { label: 'No comparison', value: COMPARE_MODES.NONE },
  { label: 'Previous period', value: COMPARE_MODES.PREV_PERIOD },
  { label: 'Previous month', value: COMPARE_MODES.PREV_MONTH },
  { label: 'Previous year', value: COMPARE_MODES.PREV_YEAR },
  { label: 'Custom range', value: COMPARE_MODES.CUSTOM },
];

export const EMPTY_MESSAGE = 'No result found, change filters';

// Shown in place of any raw backend error — the exact message from the API is
// never surfaced to the admin. A failed funnel request most often just means the
// Redshift tables are mid-refresh, so the copy points there.
export const ERROR_MESSAGE =
  'Something went wrong — the data is most likely still syncing. Please check back in a little while.';
export const COMPARE_ERROR_MESSAGE =
  'Comparison data is unavailable right now — it may still be syncing.';

// ── Category-tab (Brand/Intent/Content) chip controls ──
export const YEAR_OPTIONS = [2025, 2026];

// Period selection mode for the category-style tabs (Category/Non-Content/Blogs):
// either a month-wise breakdown (Year + Month chips) or an arbitrary custom date
// range. Only one is active at a time — see usePeriodSelection / PeriodChips.
export const PERIOD_MODES = {
  MONTH: 'month',
  RANGE: 'range',
};

export const PERIOD_MODE_OPTIONS = [
  { label: 'By month', value: PERIOD_MODES.MONTH },
  { label: 'Custom dates', value: PERIOD_MODES.RANGE },
];

// Derived from the repo-wide MONTHS constant (constants/constants.js).
export const MONTH_OPTIONS = MONTHS.map((month, index) => ({
  label: month.slice(0, 3),
  value: index,
}));

export const GRAPH_METRIC_OPTIONS = [
  { label: 'QL', value: METRICS.QL },
  { label: 'Trials Done', value: METRICS.TRIAL_DONE },
  { label: 'Enrolled', value: METRICS.ENROLLED },
];

// Distinct series colours for the top-contributors month-on-month graph.
// Distinct series colors from the shared palette (constants/colors.js).
export const PAGE_SERIES_COLORS = [
  COLORS.PRIMARY,
  COLORS.POSITIVE,
  COLORS.AMBER,
  COLORS.PURPLE,
  COLORS.DARK_PINK,
  COLORS.CERULEAN,
  COLORS.BRIGHT_GREEN,
  COLORS.YELLOW_5,
  COLORS.BITTERSWEET,
  COLORS.LIGHT_PASTEL_PURPLE,
];

export const TOP_PAGES_COUNT = 10;

// ── Non-Content drill-down ──
// Friendly labels for (source_channel, platform_channel) pairs — DB-verified
// cross-tabs. Exact pairs first, then source-prefix families, then a raw
// 'SOURCE · PLATFORM' fallback. '(unknown)' = lead whose parent has no
// user_source_log row — kept visible so nothing silently disappears.
export const NC_PAIR_LABELS = {
  'ORGANIC|WEBSITE': 'Website signups',
  'ORGANIC|CUEMATHAPP': 'Cuemath App',
  'ORGANIC|WEBSITE_CHATBOT': 'Website Chatbot',
  'ORGANIC|LEAP_ADMIN': 'Agent-created (Leap Admin)',
  'ORGANIC|ASTRO': 'Astro app',
  'ORGANIC_WHATSAPP|WHATSAPP_CHATBOT': 'WhatsApp Bot',
  'ORGANIC_SOCIAL|ASTRO': 'Social — Astro app',
  'ORGANIC_SOCIAL|WEBSITE': 'Social — website',
  'ORGANIC_SOCIAL|LEAP_ADMIN': 'Social — agent entered',
  'ORGANIC_INBOUND|LEAP_ADMIN': 'Inbound (Leap Admin)',
  'ORGANIC_TEACHER|LEAP_ADMIN': 'CueTeacher',
  'ORGANIC_TICKET|LEAP_ADMIN': 'Support ticket (Leap Admin)',
  '(unknown)|(unknown)': '(unknown)',
};

// Source-family fallbacks (any platform).
const NC_SOURCE_FAMILY_LABELS = [
  { prefix: 'ORGANIC_WHATSAPP', label: 'WhatsApp Bot' },
  { prefix: 'ORGANIC_SOCIAL', label: 'Social — other' },
  { prefix: 'PAID_AD_FB', label: 'Paid — Meta (FB/IG)' },
  { prefix: 'PAID_AD_GOOGLE', label: 'Paid — Google' },
  { prefix: 'PAID_AD_AFFILIATE', label: 'Paid — Affiliates' },
  { prefix: 'PAID_AD', label: 'Paid — Other' },
  { prefix: 'OFFLINE_EVENTS_LUMA', label: 'Offline events (Luma)' },
  { prefix: 'OFFLINE', label: 'Offline — other' },
  { prefix: 'REFERRAL_PARENT', label: 'Referral — parent' },
  { prefix: 'REFERRAL_TEACHER', label: 'Referral — teacher' },
  { prefix: 'REFERRAL', label: 'Referral — other' },
  { prefix: 'MATHFIT', label: 'MathFit' },
  { prefix: 'TEST', label: 'Test leads' },
  { prefix: 'OTHER', label: 'Other / unclassified' },
];

export const ncPairLabel = (subSource, platform) => {
  const exact = NC_PAIR_LABELS[`${subSource}|${platform}`];
  if (exact) return exact;
  const source = subSource || '(unknown)';
  const family = NC_SOURCE_FAMILY_LABELS.find(
    entry => source.indexOf(entry.prefix) === 0,
  );
  if (family) return family.label;
  return `${source} · ${platform || '(unknown)'}`;
};
